<template>
    <div class="energy-efficiency-grade">
        <div ref="pyramidChart" class="pyramidChart" style="width: 100%; height: 90%"></div>
        <div class="info-content">
            <i class="material-icons-outlined info-icon">info</i
            >{{ `단위면적당 1차에너지 소요량 (${rawData?.values?.grade?.unitInfo?.unit} · ${timeLabel})` }}
        </div>
    </div>
</template>

<script>
import * as echarts from "echarts";

export default {
    name: "xeEnergyEfficiencyGrade",
    props: {
        rawData: {
            type: Object,
            default: null,
        },
        cardHeight: {
            type: Number,
            default: 1,
        },
        cardWidth: {
            type: Number,
            default: 1,
        },
        widget: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            chart: null,
            gradeData: [
                { label: "1+++", value: 6 },
                { label: "1++", value: 10 },
                { label: "1+", value: 15 },
                { label: "1", value: 20 },
                { label: "2", value: 25 },
                { label: "3", value: 30 },
                { label: "4", value: 35 },
                { label: "5", value: 40 },
                { label: "6", value: 45 },
                { label: "7", value: 50 },
            ],
            gradeColors: {
                run: "#388E3C",
                totRun: "#1976D2",
                cert: "#546E7A",
            },
            symbol: "path://M10,0 L90,0 L90,25 L10,25 L0,12.5 Z",
        };
    },
    computed: {
        timeLabel() {
            const headers = this.rawData?.headers;
            if (!Array.isArray(headers) || !headers[0]?.timeDsvn) {
                return "-";
            }
            return headers[0].timeDsvn === "MONTH" ? "당월" : "올해";
        },
        chartSize() {
            return this.cardHeight * this.cardWidth;
        },
        dynamicSymbolSize() {
            const width = Math.max(60, Math.min(100, this.cardWidth * 10));
            const height = Math.max(16, Math.min(30, this.cardHeight * 2.5));
            return [width, height];
        },
    },
    watch: {
        rawData() {
            this.renderChart();
        },
        chartSize() {
            this.chart.resize();
        },
    },
    mounted() {
        this.renderChart();
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resizeChart);
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        selectGrade(run, cert, totRun) {
            if (!run && !cert && !totRun) {
                return { run: null, cert: null, totRun: null };
            }
            const result = { run, totRun, cert };
            if (run === totRun) {
                result.totRun = null;
            }
            if (run === cert) {
                result.cert = null;
            }
            if (totRun === cert && result.cert !== null) {
                result.cert = null;
            }
            return result;
        },
        resizeChart() {
            this.chart && this.chart.resize();
        },

        renderChart() {
            if (!this.rawData || !this.$refs.pyramidChart) return;
            // 기존 인스턴스가 있으면 dispose
            if (this.chart) {
                this.chart.dispose();
            }
            this.chart = echarts.init(this.$refs.pyramidChart);
            const { run, cert, totRun } = this.gradeColors;
            const colorGradient = [
                "#4caf50",
                "#7cb342",
                "#cddc39",
                "#ffeb3b",
                "#ffc107",
                "#ff9800",
                "#ff7043",
                "#ff5722",
                "#f44336",
                "#d32f2f",
            ];
            const measuredGrade = this.rawData?.values?.grade?.measuredGrade;
            const certifiedGrade = this.rawData?.values?.grade?.certifiedGrade;
            const totalOperatingGrade = this.rawData?.values?.grade?.totalOperatingGrade;
            const yCategories = this.gradeData.map((item) => item.label);
            const {
                run: localMeasuredGrade,
                cert: localCertifiedGrade,
                totRun: localTotalOperatingGrade,
            } = this.selectGrade(measuredGrade, certifiedGrade, totalOperatingGrade) || {};

            // markLine data 동적 생성
            const markLineData = [];
            if (localMeasuredGrade && yCategories.includes(localMeasuredGrade)) {
                markLineData.push([
                    { xAxis: 0, yAxis: localMeasuredGrade, lineStyle: { color: run, width: 1, type: "solid" } },
                    { xAxis: 55, yAxis: localMeasuredGrade, lineStyle: { color: run, width: 1, type: "solid" } },
                ]);
            }
            if (localCertifiedGrade && yCategories.includes(localCertifiedGrade)) {
                markLineData.push([
                    { xAxis: 0, yAxis: localCertifiedGrade, lineStyle: { color: cert, width: 1, type: "solid" } },
                    { xAxis: 55, yAxis: localCertifiedGrade, lineStyle: { color: cert, width: 1, type: "solid" } },
                ]);
            }
            if (localTotalOperatingGrade && yCategories.includes(localTotalOperatingGrade)) {
                markLineData.push([
                    {
                        xAxis: 0,
                        yAxis: localTotalOperatingGrade,
                        lineStyle: { color: totRun, width: 1, type: "solid" },
                    },
                    {
                        xAxis: 55,
                        yAxis: localTotalOperatingGrade,
                        lineStyle: { color: totRun, width: 1, type: "solid" },
                    },
                ]);
            }

            // markPoint data 동적 생성
            const markPointData = [];
            if (localMeasuredGrade && yCategories.includes(localMeasuredGrade)) {
                markPointData.push({
                    xAxis: 55,
                    yAxis: localMeasuredGrade,
                    value: `운영등급 (${this.timeLabel})`,
                    symbol: this.symbol,
                    symbolSize: this.dynamicSymbolSize,
                    label: {
                        show: true,
                        position: "inside",
                        formatter: `운영등급 (${this.timeLabel})`,
                        color: "#fff",
                        fontSize: this.widget.fontSize - 2 || 10,
                        fontWeight: "bold",
                    },
                    itemStyle: { color: run },
                });
            }
            if (localCertifiedGrade && yCategories.includes(localCertifiedGrade)) {
                markPointData.push({
                    xAxis: 57,
                    yAxis: localCertifiedGrade,
                    value: "인증등급",
                    symbol: this.symbol,
                    symbolSize: [this.dynamicSymbolSize[0] * 0.7, this.dynamicSymbolSize[1]],
                    label: {
                        show: true,
                        position: "inside",
                        formatter: "인증등급",
                        color: "#fff",
                        fontSize: this.widget.fontSize - 2 || 10,
                        fontWeight: "bold",
                    },
                    itemStyle: { color: cert },
                });
            }
            if (localTotalOperatingGrade && yCategories.includes(localTotalOperatingGrade)) {
                markPointData.push({
                    xAxis: 55,
                    yAxis: localTotalOperatingGrade,
                    value: "운영등급(전체)",
                    symbol: this.symbol,
                    symbolSize: this.dynamicSymbolSize,
                    label: {
                        show: true,
                        position: "inside",
                        formatter: "운영등급(전체)",
                        color: "#fff",
                        fontSize: this.widget.fontSize - 2 || 10,
                        fontWeight: "bold",
                    },
                    itemStyle: { color: totRun },
                });
            }

            const option = {
                grid: {
                    left: "5%",
                    right: "5%",
                    top: "5%",
                    bottom: "-4%",
                    containLabel: true,
                },
                tooltip: {
                    formatter: (params) => `${params.name}: ${params.value}`,
                },
                xAxis: {
                    type: "value",
                    show: false,
                    max: 60,
                },
                yAxis: {
                    type: "category",
                    inverse: true,
                    data: yCategories,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                },
                series: [
                    {
                        type: "bar",
                        tooltip: { show: false },
                        z: 10,
                        barCategoryGap: "0%",
                        barWidth: "100%",
                        data: this.gradeData.map((item, index) => ({
                            value: item.value,
                            label: item.label,
                            itemStyle: { color: colorGradient[index] },
                        })),
                        label: {
                            show: true,
                            position: "insideLeft",
                            formatter: (params) => `${params.name}`,
                            color: "#fff",
                            fontSize: this.widget.fontSize || 12,
                            fontWeight: "bold",
                        },
                        markLine: {
                            z: 30,
                            zlevel: 1,
                            tooltip: { show: false },
                            emphasis: { disabled: true },
                            lineStyle: { type: "solid", color: "#388E3C" },
                            data: markLineData,
                            label: { show: false },
                            symbol: ["none", "none"],
                            symbolSize: 10,
                        },
                        markPoint: {
                            tooltip: { show: false },
                            emphasis: { disabled: true },
                            data: markPointData,
                        },
                    },
                ],
            };
            console.log("option", option);
            this.chart.setOption(option);
            // 차트가 보이는 시점에 resize 보장
            this.$nextTick(() => {
                setTimeout(() => {
                    this.chart && this.chart.resize();
                }, 100);
            });
        },
    },
};
</script>

<style scoped>
.energy-efficiency-grade {
    width: 100%;
    height: 100%;
}
.pyramidChart {
    width: 100%;
}
.info-content {
    display: flex;
    justify-content: flex-end;
    font-weight: 600;
}
.info-icon {
    font-size: 16px;
}
</style>
